import { formatPrice } from '$store/sdk/format.ts'
import { useOffer } from '$store/sdk/useOffer.ts'
import Image from 'apps/website/components/Image.tsx'
import Icon from 'site/components/ui/Icon.tsx'
import { Signal } from '@preact/signals'
import { JSX } from 'preact'
import { Product } from 'apps/commerce/types.ts'
import { getCheapestVariantOffers } from 'site/utils/getCheapestVariantOffers.ts'
import type { AlgoliaHitInsight } from 'site/utils/algoliaInsights.ts'

export interface SearchSuggestionProduct extends Pick<Product, 'url' | 'image'> {
    isVariantOf: Pick<NonNullable<Product['isVariantOf']>, 'name'> | undefined
    offers: ReturnType<typeof useOffer>
    algoliaHit?: AlgoliaHitInsight
}

export const mapProductToSearchSuggestionProduct = (
    { url, offers, image, isVariantOf }: Product,
): SearchSuggestionProduct => {
    const offersToDisplay = isVariantOf?.hasVariant?.length
        ? getCheapestVariantOffers(isVariantOf.hasVariant) ?? offers
        : offers

    return {
        url,
        image,
        // This is not a hook
        // deno-lint-ignore react-rules-of-hooks
        offers: useOffer(offersToDisplay),
        isVariantOf: isVariantOf
            ? {
                name: isVariantOf.name,
            }
            : undefined,
    }
}

interface Props extends Omit<JSX.HTMLAttributes<HTMLUListElement>, 'class' | 'className'> {
    products: SearchSuggestionProduct[]
    searchValue: Signal<string>
    query: string
    algoliaIndex?: string
}

export default function SuggestionsProductList(
    { products, searchValue, query, algoliaIndex, ...ulProps }: Props,
) {
    const firstThreeProducts = products.slice(0, 3)
    return (
        <div
            class='flex-col items-center lg:w-[564px] pb-[10px] lg:pb-[28px]'
            {...(algoliaIndex ? { 'data-insights-index': algoliaIndex } : {})}
        >
            <span class='font-extrabold self-start font-lato mb-4 text-neutral-1 hidden lg:block'>
                Produtos em destaques
            </span>
            <ul
                {...ulProps}
                class='flex flex-col lg:flex-row gap-[1px] lg:gap-6 overflow-y-auto h-[250px] lg:h-full scrollbar'
            >
                {firstThreeProducts.map((product) => {
                    const { listPrice, price } = product.offers

                    return (
                        <li>
                            <a
                                href={product.url}
                                class='py-1 flex flex-row lg:flex-col justify-center items-center lg:items-start align-middle gap-8 lg:gap-1 w-full max-w-[375] lg:max-w-[172px]'
                                {...(product.algoliaHit
                                    ? {
                                        'data-insights-object-id': product.algoliaHit.objectId,
                                        'data-insights-position': String(product.algoliaHit.position),
                                        'data-insights-query-id': product.algoliaHit.queryId,
                                    }
                                    : {})}
                            >
                                <Image
                                    height={258}
                                    width={172}
                                    alt=''
                                    src={product?.image?.[0]?.url ?? ''}
                                    class='hidden lg:block'
                                />
                                <Image
                                    height={99}
                                    width={66}
                                    alt=''
                                    src={product?.image?.[0]?.url ?? ''}
                                    class='block lg:hidden'
                                />
                                <div class='flex flex-col'>
                                    <h3 class='font-bold text-xs lg:text-sm font-lato line-clamp-3 lg:h-[40px] text-ellipsis text-neutral-0 text-left'>
                                        {product.isVariantOf?.name}
                                    </h3>
                                    <div class='flex items-center gap-x-2 flex-row'>
                                        {listPrice && price && price > 0 && listPrice > price && (
                                            <div class='text-light text-xs text-neutral-2 line-through'>
                                                {formatPrice(listPrice)}
                                            </div>
                                        )}
                                        {price && price > 0 && (
                                            <div class='font-extrabold text-sm lg:text-base font-lato text-primary-700'>
                                                {formatPrice(price)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </a>
                        </li>
                    )
                })}
            </ul>
            <a
                href={`/s?q=${encodeURIComponent(searchValue.value || query)}`}
                class='font-bold text-neutral-2 underline mt-5 w-full items-center justify-center gap-1 hidden lg:flex'
            >
                Ver mais
                <Icon id='ArrowRight' width={8} height={15} class='text-neutral-2 mt-[3px]' />
            </a>
        </div>
    )
}
